import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { NotificationProvider } from "./components/Notifications";
import { AnalyticsTracker } from "./components/Analytics";
import WhatsAppButton from "./components/WhatsAppButton";
import InstallAppBanner from "./components/InstallAppBanner";

// Pages
import Home from "./pages/Home";
import PackageDetail from "./pages/PackageDetail";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import AdminLogin from "./pages/AdminLogin";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PaymentSuccess from "./pages/PaymentSuccess";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import WarrantiesReturns from "./pages/WarrantiesReturns";
import PartnerLanding from "./pages/PartnerLanding";
import PartnerDashboard from "./pages/PartnerDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, setUser, loading, logout, checkAuth };
};

// Protected Route Component
const ProtectedRoute = ({ children, user, loading }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user ? children : null;
};

// Admin Route Component
const AdminRoute = ({ children, user, loading, checkAuth }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/admin/login");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user && user.role === "admin" ? children : null;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  const { user, setUser, loading, logout, checkAuth } = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Emergent Auth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <NotificationProvider user={user}>
      <AnalyticsTracker />
      <Routes>
        <Route path="/" element={<Home user={user} logout={logout} />} />
        <Route path="/login" element={<Login user={user} setUser={setUser} />} />
        <Route path="/register" element={<Register user={user} setUser={setUser} />} />
        <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
        <Route path="/package/:packageId" element={<PackageDetail user={user} logout={logout} />} />
        <Route path="/payment/success" element={<PaymentSuccess user={user} logout={logout} />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <Dashboard user={user} logout={logout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute user={user} loading={loading} checkAuth={checkAuth}>
              <AdminPanel user={user} logout={logout} />
            </AdminRoute>
          }
        />
        <Route path="/admin/login" element={<AdminLogin setUser={setUser} checkAuth={checkAuth} />} />
        <Route path="/privacy" element={<PrivacyPolicy user={user} logout={logout} />} />
        <Route path="/terms" element={<TermsConditions user={user} logout={logout} />} />
        <Route path="/warranties" element={<WarrantiesReturns user={user} logout={logout} />} />
        <Route path="/partners" element={<PartnerLanding user={user} logout={logout} />} />
        <Route path="/partner/dashboard" element={<PartnerDashboard user={user} logout={logout} />} />
      </Routes>
    </NotificationProvider>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <WhatsAppButton 
        phoneNumber="34688854625" 
        message="Hola, tengo una consulta sobre BulkBytes"
      />
      <InstallAppBanner />
    </div>
  );
}

export default App;
